import type {
  AncestralDef,
  BuildingDef,
  GameState,
  JobView,
  ResourceView,
  TechDef,
} from './types';
import {
  ANCESTRAL_NODES,
  BUILDINGS,
  JOBS,
  RESOURCES,
  TECHS,
  TECHS_BY_ID,
} from './content';
import type { ProductionResult } from './production';
import { isAncestralAvailable, isBuildingUnlocked, isTechAvailable } from './actions';

/**
 * Resources worth showing: anything from an age the tribe has reached, that it
 * either holds, produces, or has produced at some point this generation.
 */
export function visibleResources(
  state: GameState,
  production: ProductionResult,
): ResourceView[] {
  const views: ResourceView[] = [];

  for (const resource of RESOURCES) {
    if (resource.age > state.ageIndex) continue;

    const amount = state.resources[resource.id] ?? 0;
    const produced = production.production[resource.id] ?? 0;
    const consumed = production.consumption[resource.id] ?? 0;
    if (amount <= 0 && produced <= 0 && consumed <= 0 && resource.age > 0) continue;

    const cap = production.caps[resource.id] ?? Infinity;
    views.push({
      def: resource,
      amount,
      cap,
      production: produced,
      consumption: consumed,
      net: produced - consumed,
      isFull: amount >= cap - 1e-6,
    });
  }

  return views;
}

/** Jobs from every age the tribe has reached, with their unlock state. */
export function visibleJobs(state: GameState, production: ProductionResult): JobView[] {
  const views: JobView[] = [];

  for (const job of JOBS) {
    if (job.age > state.ageIndex) continue;

    const unlocked =
      (!job.requiresTech || state.techs.includes(job.requiresTech)) &&
      (!job.requiresBuilding || (state.buildings[job.requiresBuilding] ?? 0) > 0);

    views.push({
      def: job,
      assigned: state.jobs[job.id] ?? 0,
      efficiency: production.jobEfficiency[job.id] ?? (state.jobs[job.id] ? 1 : 0),
      outputs: job.outputs,
      inputs: job.inputs ?? {},
      unlocked,
    });
  }

  return views;
}

export interface TechView {
  def: TechDef;
  owned: boolean;
  available: boolean;
  /** Prerequisites not yet met, for the tooltip. */
  missingRequirements: string[];
}

/** Techs for the current age plus any still-unbought techs from earlier ages. */
export function visibleTechs(state: GameState): TechView[] {
  return TECHS.filter((tech) => tech.age <= state.ageIndex).map((tech) => {
    const owned = state.techs.includes(tech.id);
    return {
      def: tech,
      owned,
      available: isTechAvailable(state, tech.id),
      missingRequirements: tech.requires
        .filter((requirement) => !state.techs.includes(requirement))
        .map((requirement) => TECHS_BY_ID[requirement]?.name ?? requirement),
    };
  });
}

export interface BuildingView {
  def: BuildingDef;
  owned: number;
  unlocked: boolean;
  atMax: boolean;
}

export function visibleBuildings(state: GameState): BuildingView[] {
  return BUILDINGS.filter((building) => building.age <= state.ageIndex).map((building) => {
    const owned = state.buildings[building.id] ?? 0;
    return {
      def: building,
      owned,
      unlocked: isBuildingUnlocked(state, building.id),
      atMax: building.maxOwned !== undefined && owned >= building.maxOwned,
    };
  });
}

export interface AncestralView {
  def: AncestralDef;
  level: number;
  available: boolean;
  atMax: boolean;
  /** Requirement node names not yet purchased. */
  missingRequirements: string[];
}

export function visibleAncestral(state: GameState): AncestralView[] {
  return ANCESTRAL_NODES.filter((node) => node.minAge <= state.ageIndex).map((node) => {
    const level = state.ancestral[node.id] ?? 0;
    return {
      def: node,
      level,
      available: isAncestralAvailable(state, node.id),
      atMax: level >= node.maxLevel,
      missingRequirements: (node.requires ?? [])
        .filter((requirement) => (state.ancestral[requirement] ?? 0) <= 0)
        .map(
          (requirement) =>
            ANCESTRAL_NODES.find((candidate) => candidate.id === requirement)?.name ?? requirement,
        ),
    };
  });
}
